import Vue from 'vue';
import * as Sentry from '@sentry/vue';
import { Globals } from '@/modules/Globals';
import { IUser } from '@/interfaces/IUser';
import { IRestaurant } from '@/interfaces/IRestaurant';

/**
 * Hardcoded on purpose: the admin deploys as a static Cloudflare Pages site
 * with no off-code secret storage, and a DSN is a public identifier (it can
 * only ingest events, never read data).
 */
const SENTRY_DSN =
  'https://5a4cb63bbb99cae46eab86ab1273a6a8@o4510739430113280.ingest.us.sentry.io/4511929916588032';

/**
 * Noise that is never actionable: connectivity blips from restaurants with
 * flaky links (axios "Network Error"/timeouts) and the benign ResizeObserver
 * loop warning. Everything else must reach Sentry.
 */
const IGNORED_ERRORS = [
  'Network Error',
  /timeout of \d+ms exceeded/,
  'Request aborted',
  /ResizeObserver loop/,
  // iOS Safari rejects WebAudio playback started without a user gesture
  // (notification sounds via howler); browser policy, not a code defect.
  'Failed to start the audio device',
  // vue-router 3 reports superseded/duplicate navigations as rejections;
  // routine during login/guard redirects, never actionable.
  /Navigation cancelled from/,
  /Redirected when going from/,
  /Avoided redundant navigation/,
];

/**
 * Must run before `new App()` so the SDK hooks Vue.config.errorHandler and
 * the global handlers before any app code executes. Localhost is excluded:
 * dev sessions would flood the project with hot-reload artifacts.
 */
export function initSentry(): void {
  if (Globals.IsLocalhost) return;

  // Monitoring must never brick the admin: any SDK init failure is logged
  // and the app boots without Sentry.
  try {
    doInit();
  } catch (e) {
    console.error('Error initializing Sentry', e);
  }
}

function doInit(): void {
  Sentry.init({
    Vue,
    dsn: SENTRY_DSN,
    release: `cartadigital-admin@${Globals.version}`,
    environment: Globals.IsProduction ? 'production' : 'preview',
    integrations: [
      Sentry.browserTracingIntegration(),
      // Unmasked replays: this is an internal back-office tool and the replay
      // content is what makes production debugging possible.
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    ignoreErrors: IGNORED_ERRORS,
    normalizeDepth: 6,
  });

  Sentry.setTag(
    'shell',
    Globals.isCapacitor
      ? Globals.isLegacyAndroidShell
        ? 'capacitor-legacy'
        : 'capacitor'
      : 'browser'
  );
}

/** Keeps the Sentry identity in sync with the session (null on logout). */
export function setSentryUser(user: IUser | null): void {
  Sentry.setUser(
    user ? { id: String(user.id), email: user.email, username: user.name } : null
  );
}

/** Tags every event with the restaurant being managed when one is active. */
export function setSentryRestaurant(restaurant: IRestaurant | null): void {
  Sentry.setTag('restaurant', restaurant ? restaurant.stringId : undefined);
  Sentry.setTag(
    'restaurantId',
    restaurant ? String(restaurant.id) : undefined
  );
}
